import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Scroll animation hook ───────────────────────────────────────────────────
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs?.observe(el);
    return () => obs?.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ─── Feature showcase data ────────────────────────────────────────────────────
const SHOWCASE_FEATURES = [
  {
    id: 1,
    title: 'Dashboard Overview',
    url: 'app.arkdai.io/dashboard',
    desc: 'Your command centre. See active sprints, team velocity, and recent activity at a glance — no digging required.',
    bullets: ['Real-time sprint metrics', 'Activity feed with context', 'Keyboard-first navigation'],
    stats: [
      { label: 'Avg. load time', value: '< 200ms' },
      { label: 'Widgets', value: '12 live' },
      { label: 'Refresh rate', value: 'Real-time' },
    ],
  },
  {
    id: 2,
    title: 'Sprint Planning',
    url: 'app.arkdai.io/sprint',
    desc: 'Fibonacci estimation, Monte Carlo forecasting, and drag-and-drop backlog management — all in one focused view.',
    bullets: ['Monte Carlo simulation', 'Fibonacci point scale', 'Team capacity tracking'],
    stats: [
      { label: 'Forecast accuracy', value: '~87%' },
      { label: 'Estimation scale', value: 'Fibonacci' },
      { label: 'Backlog items', value: 'Unlimited' },
    ],
  },
  {
    id: 3,
    title: 'Task Detail',
    url: 'app.arkdai.io/task/42',
    desc: 'Markdown editor with live preview, activity timeline, and a two-column layout that keeps context always visible.',
    bullets: ['Markdown + live preview', 'Activity timeline', 'Two-column layout'],
    stats: [
      { label: 'Editor mode', value: 'Markdown' },
      { label: 'History depth', value: 'Full log' },
      { label: 'Attachments', value: 'Supported' },
    ],
  },
  {
    id: 4,
    title: 'Analytics Dashboard',
    url: 'app.arkdai.io/analytics',
    desc: 'Velocity trends, burndown charts, cumulative flow, and GitHub commit correlation — export to PDF or CSV.',
    bullets: ['Burndown & velocity charts', 'GitHub commit correlation', 'PDF / CSV export'],
    stats: [
      { label: 'Chart types', value: '6 views' },
      { label: 'Export formats', value: 'PDF, CSV' },
      { label: 'Data range', value: 'All sprints' },
    ],
  },
  {
    id: 5,
    title: 'Team Management',
    url: 'app.arkdai.io/team',
    desc: 'Roles, workspace settings, Slack and GitHub integrations — manage your whole team without leaving Arkdai.',
    bullets: ['Role-based access', 'Slack & GitHub integrations', 'Mobile-responsive'],
    stats: [
      { label: 'Role levels', value: '4 tiers' },
      { label: 'Integrations', value: 'Slack, GitHub' },
      { label: 'Invite flow', value: 'Email-based' },
    ],
  },
  {
    id: 6,
    title: 'Authentication',
    url: 'app.arkdai.io/login',
    desc: 'Tabbed login and register, real-time password strength, OAuth via GitHub and Google — secure from day one.',
    bullets: ['OAuth (GitHub, Google)', 'Password strength meter', 'Session management'],
    stats: [
      { label: 'Auth methods', value: '3 options' },
      { label: 'Session', value: 'JWT-based' },
      { label: 'Security', value: 'RLS + policies' },
    ],
  },
];

// ─── Animated connector line ──────────────────────────────────────────────────
function ConnectorLine({ inView, delay = 0 }) {
  return (
    <div className="flex-1 h-px bg-[#e4e7f2] relative overflow-hidden mx-4">
      <div
        style={{
          position: 'absolute', top: 0, left: 0, height: '100%',
          background: '#3b4cca',
          width: inView ? '100%' : '0%',
          transition: `width 0.8s ease-out ${delay}ms`,
        }}
      />
    </div>
  );
}

// ─── Main Landing Component ───────────────────────────────────────────────────
const Landing = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [displayFeature, setDisplayFeature] = useState(0);
  const [imgFade, setImgFade] = useState(true);
  const [leftFade, setLeftFade] = useState(true);

  const showcaseSectionRef = useRef(null);
  const showcaseInnerRef = useRef(null);

  // Scroll-based nav frosted glass
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Showcase scroll detection
  useEffect(() => {
    const section = showcaseSectionRef?.current;
    if (!section) return;
    const onScroll = () => {
      const rect = section?.getBoundingClientRect();
      const totalHeight = section?.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect?.top);
      const progress = Math.min(1, scrolled / totalHeight);
      const idx = Math.min(5, Math.floor(progress * 6));
      if (idx !== activeFeature) {
        setActiveFeature(idx);
        setImgFade(false);
        setLeftFade(false);
        setTimeout(() => {
          setDisplayFeature(idx);
          setImgFade(true);
          setLeftFade(true);
        }, 220);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [activeFeature]);

  const jumpToFeature = (idx) => {
    const section = showcaseSectionRef?.current;
    if (!section) return;
    const totalHeight = section?.offsetHeight - window.innerHeight;
    const targetScroll = section?.offsetTop + (idx / 6) * totalHeight;
    window.scrollTo({ top: targetScroll, behavior: 'smooth' });
  };

  // Intersection observer refs
  const [capRef, capInView] = useInView();
  const [featRef, featInView] = useInView();
  const [showcaseTitleRef, showcaseTitleInView] = useInView();
  const [spot1Ref, spot1InView] = useInView(0.1);
  const [spot2Ref, spot2InView] = useInView(0.1);
  const [spot3Ref, spot3InView] = useInView(0.1);
  const [howRef, howInView] = useInView();
  const [kbRef, kbInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const feat = SHOWCASE_FEATURES?.[displayFeature];

  return (
    <div style={{ fontFamily: "'Outfit', sans-serif", background: '#f7f8fc', color: '#0d0f1c' }}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .arkdai-btn-primary {
          background: #3b4cca;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 12px 28px;
          font-family: 'Outfit', sans-serif;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          transition: background 180ms ease, box-shadow 180ms ease;
          box-shadow: 0 4px 18px rgba(59,76,202,0.22);
        }
        .arkdai-btn-primary:hover { background: #2d3aaa; box-shadow: 0 6px 24px rgba(59,76,202,0.32); }

        .arkdai-btn-ghost {
          background: transparent;
          color: #0d0f1c;
          border: 1.5px solid #e4e7f2;
          border-radius: 10px;
          padding: 12px 28px;
          font-family: 'Outfit', sans-serif;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          transition: border-color 180ms ease, background 180ms ease;
        }
        .arkdai-btn-ghost:hover { border-color: #3b4cca; background: rgba(59,76,202,0.04); }

        .arkdai-btn-ghost-sm {
          background: transparent;
          color: #0d0f1c;
          border: 1.5px solid #e4e7f2;
          border-radius: 8px;
          padding: 8px 18px;
          font-family: 'Outfit', sans-serif;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: border-color 180ms ease, background 180ms ease;
        }
        .arkdai-btn-ghost-sm:hover { border-color: #3b4cca; background: rgba(59,76,202,0.04); }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes pulse-ring {
          0% { transform: scale(0.85); opacity: 0.7; }
          70% { transform: scale(1.4); opacity: 0; }
          100% { transform: scale(0.85); opacity: 0; }
        }
        @keyframes draw-line { from { width: 0; } to { width: 100%; } }

        @media (prefers-reduced-motion: no-preference) {
          .hero-mockup { animation: float 5s ease-in-out infinite; }
          .blink-dot { animation: blink 1.4s ease-in-out infinite; }
          .pulse-ring { animation: pulse-ring 2.2s ease-out infinite; }
        }

        .fade-up { opacity: 0; transform: translateY(20px); transition: opacity 0.55s ease-out, transform 0.55s ease-out; }
        .fade-up.visible { opacity: 1; transform: translateY(0); }
        .slide-left { opacity: 0; transform: translateX(-32px); transition: opacity 0.6s ease-out, transform 0.6s ease-out; }
        .slide-left.visible { opacity: 1; transform: translateX(0); }
        .slide-right { opacity: 0; transform: translateX(32px); transition: opacity 0.6s ease-out, transform 0.6s ease-out; }
        .slide-right.visible { opacity: 1; transform: translateX(0); }

        .feature-card {
          background: #fff;
          border: 1.5px solid #e4e7f2;
          border-radius: 16px;
          padding: 32px;
          position: relative;
          overflow: hidden;
          transition: transform 180ms ease, box-shadow 180ms ease;
        }
        .feature-card::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 3px;
          background: #3b4cca;
          transform: scaleY(0);
          transform-origin: bottom;
          transition: transform 180ms ease;
        }
        .feature-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(59,76,202,0.12); }
        .feature-card:hover::before { transform: scaleY(1); }
        .feature-card:hover .feat-icon { transform: scale(1.12); }
        .feat-icon { transition: transform 180ms ease; }

        .kb-row { opacity: 0; transform: translateY(10px); transition: opacity 0.4s ease-out, transform 0.4s ease-out; }
        .kb-row.visible { opacity: 1; transform: translateY(0); }

        .dot-grid {
          background-image: radial-gradient(circle, #c5caf0 1px, transparent 1px);
          background-size: 28px 28px;
        }

        @media (max-width: 900px) {
          .showcase-desktop { display: none !important; }
          .showcase-mobile { display: block !important; }
          .spotlight-row { flex-direction: column !important; }
          .spotlight-row.reversed { flex-direction: column !important; }
          .how-steps { flex-direction: column !important; align-items: center !important; }
          .how-connector { display: none !important; }
          .cap-strip { flex-wrap: wrap !important; }
          .cap-divider { display: none !important; }
          .nav-links { display: none !important; }
          .hero-btns { flex-direction: column !important; align-items: center !important; }
        }
      `}</style>
      {/* ── 1. NAVIGATION ─────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        height: 64,
        display: 'flex', alignItems: 'center',
        padding: '0 40px',
        background: scrolled ? 'rgba(247,248,252,0.82)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(228,231,242,0.7)' : '1px solid transparent',
        transition: 'background 0.3s ease, border-color 0.3s ease',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#3b4cca', letterSpacing: '-0.5px' }}>Ark</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#0d0f1c', letterSpacing: '-0.5px' }}>dai</span>
        </div>
        {/* Center links */}
        <div className="nav-links" style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 36 }}>
          {['Features', 'Product Tour', 'How It Works']?.map(link => (
            <a key={link}
              href={`#${link?.toLowerCase()?.replace(/ /g, '-')}`}
              style={{ color: '#4b5166', fontSize: 14, fontWeight: 500, textDecoration: 'none', transition: 'color 180ms ease' }}
              onMouseEnter={e => e.target.style.color = '#0d0f1c'}
              onMouseLeave={e => e.target.style.color = '#4b5166'}
            >{link}</a>
          ))}
        </div>
        {/* Right CTAs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
          <button className="arkdai-btn-ghost-sm" onClick={() => navigate('/login-register')}>Sign In</button>
          <button className="arkdai-btn-primary" style={{ padding: '8px 20px', fontSize: 14 }} onClick={() => navigate('/login-register')}>Get Early Access</button>
        </div>
      </nav>
      {/* ── 2. HERO ───────────────────────────────────────────────────────── */}
      <section style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        paddingTop: 80, paddingBottom: 60, paddingLeft: 24, paddingRight: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Dot grid bg */}
        <div className="dot-grid" style={{
          position: 'absolute', inset: 0, zIndex: 0,
          maskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%, black 30%, transparent 100%)',
        }} />
        {/* Indigo radial glow */}
        <div style={{
          position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
          width: 700, height: 500,
          background: 'radial-gradient(ellipse at center, rgba(59,76,202,0.10) 0%, transparent 70%)',
          zIndex: 0, pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 760 }}>
          {/* Eyebrow badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(59,76,202,0.08)', border: '1px solid rgba(59,76,202,0.18)',
            borderRadius: 100, padding: '6px 16px', marginBottom: 28,
          }}>
            <span className="blink-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: '#00a896', display: 'inline-block' }} />
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 500, color: '#3b4cca', letterSpacing: '0.06em' }}>EARLY ACCESS — NOW OPEN</span>
          </div>
          {/* Headline */}
          <h1 style={{ fontSize: 'clamp(38px, 6vw, 68px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.5px', marginBottom: 20 }}>
            Your team's work,{' '}
            <span style={{ color: '#3b4cca' }}>finally in one place.</span>
          </h1>
          {/* Subheadline */}
          <p style={{ fontSize: 18, color: '#4b5166', lineHeight: 1.65, marginBottom: 36, maxWidth: 560, margin: '0 auto 36px' }}>
            Plan sprints, track tasks, and ship faster — with tools built around how engineering teams actually work.
          </p>
          {/* CTAs */}
          <div className="hero-btns" style={{ display: 'flex', gap: 14, justifyContent: 'center', marginBottom: 56 }}>
            <button className="arkdai-btn-primary" style={{ fontSize: 16, padding: '14px 32px' }} onClick={() => navigate('/login-register')}>Get Early Access</button>
            <button className="arkdai-btn-ghost" style={{ fontSize: 16, padding: '14px 32px' }} onClick={() => navigate('/dashboard-overview')}>See Product Tour</button>
          </div>
          {/* Browser mockup */}
          <div className="hero-mockup" style={{ maxWidth: 820, margin: '0 auto' }}>
            <div style={{
              background: '#fff',
              border: '1.5px solid #e4e7f2',
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 24px 80px rgba(59,76,202,0.14), 0 4px 20px rgba(59,76,202,0.08)',
            }}>
              {/* Browser chrome */}
              <div style={{ background: '#f0f1f8', borderBottom: '1px solid #e4e7f2', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['#ff5f57','#febc2e','#28c840']?.map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />)}
                </div>
                <div style={{ flex: 1, background: '#e4e7f2', borderRadius: 6, height: 22, display: 'flex', alignItems: 'center', paddingLeft: 10, marginLeft: 8 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#8c93b0' }}>app.arkdai.io/dashboard</span>
                </div>
              </div>
              {/* Dashboard content */}
              <div style={{ padding: 20, background: '#f7f8fc' }}>
                {/* Metric cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                  {[
                    { label: 'Active Sprints', value: '3', color: '#3b4cca' },
                    { label: 'Tasks Done', value: '47', color: '#00a896' },
                    { label: 'Velocity', value: '34 pts', color: '#3b4cca' },
                    { label: 'Sprint Health', value: '92%', color: '#00a896' },
                  ]?.map(m => (
                    <div key={m?.label} style={{ background: '#fff', border: '1px solid #e4e7f2', borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, color: '#8c93b0', marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>{m?.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: m?.color }}>{m?.value}</div>
                    </div>
                  ))}
                </div>
                {/* Bar chart */}
                <div style={{ background: '#fff', border: '1px solid #e4e7f2', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#4b5166', marginBottom: 12 }}>Velocity — Last 8 Sprints</div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 64 }}>
                    {[22, 28, 31, 26, 34, 38, 34, 41]?.map((v, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: '100%', background: i === 7 ? '#3b4cca' : 'rgba(59,76,202,0.25)', borderRadius: '4px 4px 0 0', height: `${(v / 45) * 56}px` }} />
                        <span style={{ fontSize: 9, color: '#8c93b0', fontFamily: "'DM Mono', monospace" }}>S{i + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Activity feed */}
                <div style={{ background: '#fff', border: '1px solid #e4e7f2', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#4b5166', marginBottom: 10 }}>Recent Activity</div>
                  {[
                    { user: 'AK', action: 'moved TASK-42 to In Review', time: '2m ago', color: '#3b4cca' },
                    { user: 'ML', action: 'closed Sprint 8 with 41 pts', time: '18m ago', color: '#00a896' },
                    { user: 'RJ', action: 'added 3 tasks to backlog', time: '1h ago', color: '#3b4cca' },
                  ]?.map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: i < 2 ? '1px solid #f0f1f8' : 'none' }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: a?.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#fff' }}>{a?.user}</span>
                      </div>
                      <span style={{ fontSize: 11, color: '#4b5166', flex: 1 }}>{a?.action}</span>
                      <span style={{ fontSize: 10, color: '#8c93b0', fontFamily: "'DM Mono', monospace" }}>{a?.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── 3. CAPABILITY STRIP ───────────────────────────────────────────── */}
      <div ref={capRef} style={{ background: '#fff', borderTop: '1px solid #e4e7f2', borderBottom: '1px solid #e4e7f2' }}>
        <div className="cap-strip" style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 56 }}>
          {[
            { icon: '⚡', label: 'Sprint Planning' },
            { icon: '↕️', label: 'Drag & Drop Tasks' },
            { icon: '📈', label: 'Analytics Suite' },
            { icon: '🔗', label: 'GitHub Integration' },
            { icon: '💬', label: 'Slack Notifications' },
          ]?.map((item, i) => (
            <React.Fragment key={item?.label}>
              <div
                className={`fade-up${capInView ? ' visible' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: 7, transitionDelay: `${i * 60}ms`, whiteSpace: 'nowrap', padding: '0 20px' }}
              >
                <span style={{ fontSize: 15 }}>{item?.icon}</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 500, color: '#4b5166' }}>{item?.label}</span>
              </div>
              {i < 4 && <div className="cap-divider" style={{ width: 1, height: 20, background: '#e4e7f2', flexShrink: 0 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* ── 4. FEATURES GRID ──────────────────────────────────────────────── */}
      <section id="features" style={{ padding: '96px 24px', background: '#f7f8fc' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.8px', marginBottom: 14 }}>
              Built for the way engineers actually work
            </h2>
            <p style={{ fontSize: 17, color: '#4b5166', maxWidth: 520, margin: '0 auto' }}>
              Every feature was designed around real engineering workflows — not generic project management.
            </p>
          </div>
          <div ref={featRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            {[
              { icon: '🔐', title: 'Smart Authentication', desc: 'Tabbed login and register, real-time password strength indicator, and OAuth via GitHub and Google.' },
              { icon: '📊', title: 'Command Dashboard', desc: 'Metrics, activity feed, sprint charts, and keyboard shortcuts — your team\'s command centre.' },
              { icon: '🏃', title: 'Sprint Planning', desc: 'Fibonacci points, Monte Carlo forecasting, drag-and-drop backlog, and team capacity tracking.' },
              { icon: '📝', title: 'Rich Task Editor', desc: 'Markdown with live preview, activity timeline, and a two-column layout that keeps context visible.' },
              { icon: '📈', title: 'Analytics Suite', desc: 'Velocity, burndown, cumulative flow, GitHub correlation, and PDF/CSV export in one view.' },
              { icon: '👥', title: 'Team Management', desc: 'Roles, workspace settings, Slack and GitHub integrations — mobile-responsive throughout.' },
            ]?.map((f, i) => (
              <div
                key={f?.title}
                className={`feature-card fade-up${featInView ? ' visible' : ''}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="feat-icon" style={{ fontSize: 28, marginBottom: 16 }}>{f?.icon}</div>
                <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{f?.title}</h3>
                <p style={{ fontSize: 14, color: '#4b5166', lineHeight: 1.6 }}>{f?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── 5. SCROLLING FEATURE SHOWCASE ─────────────────────────────────── */}
      <section id="product-tour" style={{ background: '#fff', padding: '80px 0 0' }}>
        <div ref={showcaseTitleRef} style={{ textAlign: 'center', padding: '0 24px 60px' }}>
          <h2 className={`fade-up${showcaseTitleInView ? ' visible' : ''}`} style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.8px', marginBottom: 12 }}>
            Everything, beautifully connected.
          </h2>
          <p className={`fade-up${showcaseTitleInView ? ' visible' : ''}`} style={{ fontSize: 16, color: '#4b5166', transitionDelay: '60ms' }}>
            Scroll to explore each feature.
          </p>
        </div>

        {/* Desktop showcase */}
        <div className="showcase-desktop" ref={showcaseSectionRef} style={{ height: '600vh', position: 'relative' }}>
          <div style={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', padding: '0 24px', display: 'grid', gridTemplateColumns: '26% 48% 26%', gap: 0, alignItems: 'center', height: '100%' }}>
              {/* Left panel */}
              <div style={{ textAlign: 'right', paddingRight: 40, paddingTop: 60 }}>
                <div style={{
                  opacity: leftFade ? 1 : 0,
                  transform: leftFade ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.45s ease, transform 0.45s ease',
                }}>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#3b4cca', letterSpacing: '0.08em', marginBottom: 10, textTransform: 'uppercase' }}>Feature {feat?.id} of 6</div>
                  <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px', marginBottom: 12 }}>{feat?.title}</h3>
                  <p style={{ fontSize: 14, color: '#4b5166', lineHeight: 1.65, marginBottom: 20 }}>{feat?.desc}</p>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                    {feat?.bullets?.map(b => (
                      <li key={b} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: 13, color: '#4b5166' }}>{b}</span>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b4cca', flexShrink: 0 }} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Center: browser frame */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 60 }}>
                <div style={{
                  width: '100%',
                  background: '#fff',
                  border: '1.5px solid #e4e7f2',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(59,76,202,0.12)',
                }}>
                  {/* Browser chrome */}
                  <div style={{ background: '#f0f1f8', borderBottom: '1px solid #e4e7f2', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {['#ff5f57','#febc2e','#28c840']?.map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
                    </div>
                    <div style={{ flex: 1, background: '#e4e7f2', borderRadius: 5, height: 20, display: 'flex', alignItems: 'center', paddingLeft: 10, marginLeft: 6 }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#8c93b0' }}>{feat?.url}</span>
                    </div>
                  </div>
                  {/* Screenshot placeholder */}
                  <div
                    id={`feat-img-${feat?.id}`}
                    style={{
                      width: '100%', height: 320,
                      background: 'rgba(59,76,202,0.03)',
                      border: '2px dashed rgba(59,76,202,0.25)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                      opacity: imgFade ? 1 : 0,
                      transition: 'opacity 0.45s ease',
                    }}
                  >
                    <span style={{ fontSize: 32 }}>🖼️</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#8c93b0' }}>{feat?.title}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#c5caf0' }}>Replace with screenshot</span>
                  </div>
                </div>
                {/* Navigation dots */}
                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  {SHOWCASE_FEATURES?.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => jumpToFeature(i)}
                      style={{
                        width: i === displayFeature ? 24 : 8,
                        height: 8,
                        borderRadius: 4,
                        background: i === displayFeature ? '#3b4cca' : '#e4e7f2',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        padding: 0,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Right panel */}
              <div style={{ paddingLeft: 40, paddingTop: 60 }}>
                <div style={{
                  opacity: leftFade ? 1 : 0,
                  transform: leftFade ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.45s ease, transform 0.45s ease',
                }}>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#8c93b0', letterSpacing: '0.08em', marginBottom: 14, textTransform: 'uppercase' }}>Stats</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {feat?.stats?.map(s => (
                      <div key={s?.label} style={{
                        background: '#f7f8fc',
                        border: '1px solid #e4e7f2',
                        borderRadius: 10,
                        padding: '12px 16px',
                      }}>
                        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#8c93b0', marginBottom: 4 }}>{s?.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#3b4cca' }}>{s?.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile showcase — stacked cards */}
        <div className="showcase-mobile" style={{ display: 'none', padding: '0 20px 60px' }}>
          {SHOWCASE_FEATURES?.map((f, i) => (
            <div key={f?.id} style={{ marginBottom: 32, background: '#fff', border: '1.5px solid #e4e7f2', borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 20px rgba(59,76,202,0.08)' }}>
              <div style={{ background: '#f0f1f8', borderBottom: '1px solid #e4e7f2', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  {['#ff5f57','#febc2e','#28c840']?.map(c => <div key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />)}
                </div>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#8c93b0' }}>{f?.url}</span>
              </div>
              <div id={`feat-img-${f?.id}-mobile`} style={{ width: '100%', height: 180, background: 'rgba(59,76,202,0.03)', border: '2px dashed rgba(59,76,202,0.2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <span style={{ fontSize: 24 }}>🖼️</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#8c93b0' }}>{f?.title}</span>
              </div>
              <div style={{ padding: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{f?.title}</h3>
                <p style={{ fontSize: 15, color: '#4b5166', lineHeight: 1.7, marginBottom: 20 }}>{f?.desc}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {f?.stats?.map(s => (
                    <div key={s?.label} style={{ background: '#f7f8fc', border: '1px solid #e4e7f2', borderRadius: 8, padding: '6px 10px' }}>
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: '#8c93b0' }}>{s?.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#3b4cca' }}>{s?.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* ── 6. FEATURE SPOTLIGHTS ─────────────────────────────────────────── */}
      <section style={{ background: '#f7f8fc', padding: '96px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 96 }}>

          {/* Spotlight 1 — Sprint Planning */}
          <div ref={spot1Ref} className="spotlight-row" style={{ display: 'flex', alignItems: 'center', gap: 64 }}>
            {/* Text left */}
            <div className={`slide-left${spot1InView ? ' visible' : ''}`} style={{ flex: 1 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#3b4cca', letterSpacing: '0.08em', marginBottom: 10, textTransform: 'uppercase' }}>Sprint Planning</div>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, letterSpacing: '-0.6px', marginBottom: 16 }}>Stop estimating blindly.</h2>
              <p style={{ fontSize: 15, color: '#4b5166', lineHeight: 1.7, marginBottom: 20 }}>
                Monte Carlo simulation runs on your team's real historical velocity — not guesses. Get a probability distribution of when your sprint will complete, and plan capacity with Fibonacci points your team already understands.
              </p>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Fibonacci estimation scale', 'Monte Carlo probability forecasting', 'Drag-and-drop backlog ordering', 'Team capacity tracking per sprint']?.map(b => (
                  <li key={b} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00a896', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: '#4b5166' }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Visual right — mini kanban */}
            <div className={`slide-right${spot1InView ? ' visible' : ''}`} style={{ flex: 1 }}>
              <div style={{ background: '#fff', border: '1.5px solid #e4e7f2', borderRadius: 16, padding: 24, boxShadow: '0 8px 32px rgba(59,76,202,0.10)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#4b5166', marginBottom: 16 }}>Sprint 9 — Backlog</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {[
                    { col: 'Todo', color: '#e4e7f2', tasks: [
                      { label: 'TASK-51', pts: 5, color: '#3b4cca' },
                      { label: 'TASK-52', pts: 3, color: '#3b4cca' },
                      { label: 'TASK-53', pts: 8, color: '#8c93b0' },
                    ]},
                    { col: 'In Progress', color: '#fff3cd', tasks: [
                      { label: 'TASK-48', pts: 5, color: '#f59e0b' },
                      { label: 'TASK-49', pts: 2, color: '#f59e0b' },
                    ]},
                    { col: 'Done', color: '#d1fae5', tasks: [
                      { label: 'TASK-44', pts: 3, color: '#00a896' },
                      { label: 'TASK-45', pts: 5, color: '#00a896' },
                      { label: 'TASK-46', pts: 8, color: '#00a896' },
                    ]},
                  ]?.map(col => (
                    <div key={col?.col}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#4b5166', marginBottom: 8, fontFamily: "'DM Mono', monospace" }}>{col?.col}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {col?.tasks?.map(t => (
                          <div key={t?.label} style={{ background: '#f7f8fc', border: `1.5px solid ${col?.color}`, borderRadius: 8, padding: '7px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: t?.color, fontWeight: 600 }}>{t?.label}</span>
                            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, background: t?.color, color: '#fff', borderRadius: 4, padding: '1px 5px' }}>{t?.pts}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Spotlight 2 — Analytics (reversed) */}
          <div ref={spot2Ref} className="spotlight-row reversed" style={{ display: 'flex', alignItems: 'center', gap: 64, flexDirection: 'row-reverse' }}>
            {/* Text right */}
            <div className={`slide-right${spot2InView ? ' visible' : ''}`} style={{ flex: 1 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#3b4cca', letterSpacing: '0.08em', marginBottom: 10, textTransform: 'uppercase' }}>Analytics</div>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, letterSpacing: '-0.6px', marginBottom: 16 }}>Know your sprint health early.</h2>
              <p style={{ fontSize: 15, color: '#4b5166', lineHeight: 1.7, marginBottom: 20 }}>
                Real-time burndown charts update as tasks move. GitHub commit correlation shows you when code activity diverges from task progress — before it becomes a problem.
              </p>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Velocity trend over all sprints', 'Burndown with ideal vs actual line', 'Cumulative flow diagram', 'GitHub commit correlation']?.map(b => (
                  <li key={b} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00a896', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: '#4b5166' }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Visual left — analytics preview */}
            <div className={`slide-left${spot2InView ? ' visible' : ''}`} style={{ flex: 1 }}>
              <div style={{ background: '#fff', border: '1.5px solid #e4e7f2', borderRadius: 16, padding: 24, boxShadow: '0 8px 32px rgba(59,76,202,0.10)' }}>
                {/* KPI row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: 'Velocity', value: '38 pts', delta: '+12%', up: true },
                    { label: 'Cycle Time', value: '2.4d', delta: '-8%', up: false },
                    { label: 'GitHub Commits', value: '142', delta: '+24', up: true },
                  ]?.map(k => (
                    <div key={k?.label} style={{ background: '#f7f8fc', border: '1px solid #e4e7f2', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: '#8c93b0', marginBottom: 4 }}>{k?.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#0d0f1c', marginBottom: 2 }}>{k?.value}</div>
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: k?.up ? '#00a896' : '#3b4cca' }}>{k?.delta}</div>
                    </div>
                  ))}
                </div>
                {/* Burndown SVG */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#4b5166', marginBottom: 8 }}>Burndown — Sprint 9</div>
                  <svg viewBox="0 0 280 80" style={{ width: '100%', height: 80 }}>
                    <defs>
                      <linearGradient id="burnGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b4cca" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#3b4cca" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {/* Ideal line */}
                    <line x1="0" y1="10" x2="280" y2="70" stroke="#e4e7f2" strokeWidth="1.5" strokeDasharray="4 3" />
                    {/* Actual burndown */}
                    <polyline points="0,10 35,18 70,28 105,32 140,42 175,48 210,55 245,62 280,68" fill="none" stroke="#3b4cca" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <polygon points="0,10 35,18 70,28 105,32 140,42 175,48 210,55 245,62 280,68 280,80 0,80" fill="url(#burnGrad)" />
                  </svg>
                </div>
                {/* Throughput bars */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#4b5166', marginBottom: 8 }}>Throughput</div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 40 }}>
                    {[6, 9, 7, 11, 8, 13, 10, 14]?.map((v, i) => (
                      <div key={i} style={{ flex: 1, background: i === 7 ? '#00a896' : 'rgba(0,168,150,0.25)', borderRadius: '3px 3px 0 0', height: `${(v / 16) * 36}px` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spotlight 3 — Integrations */}
          <div ref={spot3Ref} className="spotlight-row" style={{ display: 'flex', alignItems: 'center', gap: 64 }}>
            {/* Text left */}
            <div className={`slide-left${spot3InView ? ' visible' : ''}`} style={{ flex: 1 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#3b4cca', letterSpacing: '0.08em', marginBottom: 10, textTransform: 'uppercase' }}>Integrations</div>
              <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, letterSpacing: '-0.6px', marginBottom: 16 }}>Integrations your devs will actually use.</h2>
              <p style={{ fontSize: 15, color: '#4b5166', lineHeight: 1.7, marginBottom: 20 }}>
                Connect GitHub to correlate commits with tasks automatically. Link Slack to get sprint updates and task notifications where your team already communicates — no extra tabs, no extra logins.
              </p>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['GitHub commit → task correlation', 'Slack sprint notifications', 'OAuth-based connection flow', 'Disconnect anytime from settings']?.map(b => (
                  <li key={b} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00a896', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: '#4b5166' }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Visual right — integration cards */}
            <div className={`slide-right${spot3InView ? ' visible' : ''}`} style={{ flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { name: 'GitHub', desc: 'Commit correlation & PR tracking', icon: '🐙', status: 'Connected', statusColor: '#00a896' },
                  { name: 'Slack', desc: 'Sprint updates & task notifications', icon: '💬', status: 'Connect', statusColor: '#3b4cca' },
                ]?.map(intg => (
                  <div key={intg?.name} style={{ background: '#fff', border: '1.5px solid #e4e7f2', borderRadius: 14, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 4px 16px rgba(59,76,202,0.07)' }}>
                    <div style={{ width: 48, height: 48, background: '#f7f8fc', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{intg?.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>{intg?.name}</div>
                      <div style={{ fontSize: 13, color: '#4b5166' }}>{intg?.desc}</div>
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 600, color: intg?.statusColor, background: `${intg?.statusColor}18`, borderRadius: 6, padding: '4px 10px', flexShrink: 0 }}>{intg?.status}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── 7. HOW IT WORKS ───────────────────────────────────────────────── */}
      <section id="how-it-works" style={{ background: '#fff', padding: '96px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.8px', marginBottom: 12 }}>Up and running in minutes</h2>
            <p style={{ fontSize: 16, color: '#4b5166' }}>No lengthy onboarding. No sales calls. Just start.</p>
          </div>
          <div ref={howRef} className="how-steps" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
            {[
              { num: '01', icon: '🏗️', title: 'Create your workspace', desc: 'Sign up, name your workspace, and invite your team in under two minutes.' },
              { num: '02', icon: '📋', title: 'Plan your first sprint', desc: 'Add tasks to the backlog, estimate with Fibonacci points, and set your sprint goal.' },
              { num: '03', icon: '🚀', title: 'Ship and review', desc: 'Track progress in real time, run your retrospective, and improve with every sprint.' },
            ]?.map((step, i) => (
              <React.Fragment key={step?.num}>
                <div
                  className={`fade-up${howInView ? ' visible' : ''}`}
                  style={{ flex: 1, textAlign: 'center', maxWidth: 240, transitionDelay: `${i * 120}ms` }}
                >
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: '#3b4cca', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 14px',
                    fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 600,
                  }}>{step?.num}</div>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{step?.icon}</div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{step?.title}</h3>
                  <p style={{ fontSize: 13, color: '#4b5166', lineHeight: 1.6 }}>{step?.desc}</p>
                </div>
                {i < 2 && (
                  <div className="how-connector" style={{ flex: 0.4, display: 'flex', alignItems: 'center', paddingTop: 26 }}>
                    <ConnectorLine inView={howInView} delay={i * 120 + 200} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
      {/* ── 8. KEYBOARD SHORTCUTS ─────────────────────────────────────────── */}
      <section style={{ background: '#f7f8fc', padding: '96px 24px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, letterSpacing: '-0.8px', marginBottom: 12 }}>Built for the keyboard</h2>
          <p style={{ fontSize: 16, color: '#4b5166', marginBottom: 40 }}>Every action has a shortcut. Your mouse is optional.</p>
          <div
            ref={kbRef}
            style={{
              background: '#0d0f1c',
              borderRadius: 16,
              padding: '32px 36px',
              maxWidth: 560,
              margin: '0 auto',
              boxShadow: '0 20px 60px rgba(13,15,28,0.3)',
            }}
          >
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#4b5166', marginBottom: 20, textAlign: 'left', letterSpacing: '0.06em' }}>// keyboard shortcuts</div>
            {[
              { keys: ['Ctrl', 'N'], action: 'Create new task' },
              { keys: ['Ctrl', 'S'], action: 'Open sprint view' },
              { keys: ['Ctrl', 'I'], action: 'View insights' },
              { keys: ['Ctrl', 'A'], action: 'Open analytics' },
              { keys: ['⌘', 'K'], action: 'Command palette' },
            ]?.map((row, i) => (
              <div
                key={row?.action}
                className={`kb-row${kbInView ? ' visible' : ''}`}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.06)' : 'none', transitionDelay: `${i * 60}ms` }}
              >
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#8c93b0' }}>{row?.action}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {row?.keys?.map(k => (
                    <kbd key={k} style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 11, fontWeight: 600,
                      color: '#00a896',
                      background: 'rgba(0,168,150,0.12)',
                      border: '1px solid rgba(0,168,150,0.3)',
                      borderRadius: 6,
                      padding: '3px 8px',
                      boxShadow: '0 2px 0 rgba(0,168,150,0.2)',
                    }}>{k}</kbd>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── 9. EARLY ACCESS CTA ───────────────────────────────────────────── */}
      <section ref={ctaRef} style={{
        background: '#eef0ff',
        border: '1px solid rgba(59,76,202,0.15)',
        borderLeft: 'none', borderRight: 'none',
        padding: '96px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Pulse glow */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500, height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,76,202,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div className="pulse-ring" style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400, height: 400,
          borderRadius: '50%',
          border: '1px solid rgba(59,76,202,0.15)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
          <div className={`fade-up${ctaInView ? ' visible' : ''}`}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.8px', marginBottom: 16 }}>
              Be among the first teams to try it.
            </h2>
            <p style={{ fontSize: 16, color: '#4b5166', lineHeight: 1.65, marginBottom: 36 }}>
              We're opening early access to a limited number of teams. No credit card, no commitment.
            </p>
            <button
              className="arkdai-btn-primary"
              style={{ fontSize: 17, padding: '16px 40px' }}
              onClick={() => navigate('/login-register')}
            >
              Request Early Access →
            </button>
          </div>
        </div>
      </section>
      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer style={{
        background: '#fff',
        borderTop: '1px solid #e4e7f2',
        boxShadow: '0 -1px 0 rgba(59,76,202,0.08)',
        padding: '28px 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: '#3b4cca' }}>Ark</span>
          <span style={{ fontSize: 18, fontWeight: 800, color: '#0d0f1c' }}>dai</span>
        </div>
        {/* Nav links */}
        <div style={{ display: 'flex', gap: 28 }}>
          {['Features', 'Product Tour', 'How It Works']?.map(link => (
            <a key={link}
              href={`#${link?.toLowerCase()?.replace(/ /g, '-')}`}
              style={{ fontSize: 13, color: '#8c93b0', textDecoration: 'none', transition: 'color 180ms ease' }}
              onMouseEnter={e => e.target.style.color = '#0d0f1c'}
              onMouseLeave={e => e.target.style.color = '#8c93b0'}
            >{link}</a>
          ))}
        </div>
        {/* Tagline */}
        <div style={{ fontSize: 13, color: '#8c93b0' }}>Built with ❤️ for engineering teams</div>
      </footer>
    </div>
  );
};

export default Landing;
